import React from 'react';
import './CompetitionCard.css';

const CompetitionCard = ({
  competition,
  onClick,
  onEdit,
  onParticipate,
  onViewMediaSources,
  onViewSubmissions,
  canEdit = false,
  showFullActions = true,
  children
}) => {
  if (!competition) {
    return null;
  }

  const handleCardClick = () => {
    if (typeof onClick === 'function') {
      onClick(competition.id);
    }
  };

  return (
    <div
      className={`competition-card theme-${getCardTheme(competition)}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleCardClick();
        }
      }}
    >
      {competition.bannerImage ? (
        <div className="card-image">
          <img src={competition.bannerImage} alt={competition.name} />
        </div>
      ) : (
        <div className="card-image placeholder">
          <div className="placeholder-content">
            <span className="placeholder-icon">{getCategoryIcon(competition.category)}</span>
            <div>
              <strong>{formatCategoryLabel(competition.category)}</strong>
              <p>Create videos, grow your reach</p>
            </div>
          </div>
        </div>
      )}

      <div className="card-content">
        <div className="card-header">
          <h3>{competition.name}</h3>
          <span className={`status-badge ${getStatusBadgeClass(competition.status)}`}>
            {competition.status}
          </span>
        </div>

        {Number(competition.totalSubmissions || 0) < 20 && (
          <div className="momentum-tag">🚀 Early Stage • High Opportunity</div>
        )}

        <p className="card-hook">Create videos using this sound and grow your reach 🚀</p>

        <div className="benefits-list">
          <div className="benefit-item">🏆 Top {competition.shortlistSize || 20} will be shortlisted</div>
          <div className="benefit-item">🎯 Multiple submissions allowed</div>
          <div className="benefit-item">🔥 Fair chance for new creators</div>
        </div>

        <div className="card-info">
          <div className="info-item">
            <span className="icon">📅</span>
            <span>{formatDate(competition.startDate)} - {formatDate(competition.endDate)}</span>
          </div>

          <div className="info-item">
            <span className="icon">⏳</span>
            <span>{getParticipationUrgency(competition.endDate)}</span>
          </div>

          {competition.requiredHashtag && (
            <div className="info-item">
              <span className="icon">#</span>
              <span>Use hashtag: {formatHashtag(competition.requiredHashtag)}</span>
            </div>
          )}

          {competition.prizes && competition.prizes.length > 0 && (
            <div className="info-item">
              <span className="icon">🏆</span>
              <span>{competition.prizes.length} Prizes</span>
            </div>
          )}
        </div>

        {competition.platform && (
          <div className="platform-badge">
            {competition.platform === 'youtube' && '▶️ YouTube'}
            {competition.platform === 'instagram' && '📷 Instagram'}
            {competition.platform === 'tiktok' && '🎵 TikTok'}
            {competition.platform === 'twitter' && '🐦 Twitter'}
          </div>
        )}

        {children || (showFullActions && (
          <>
            {(competition.status === 'draft' || competition.status === 'active') && canEdit && typeof onEdit === 'function' && (
              <button
                type="button"
                className="action-button"
                style={{ marginTop: 10 }}
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(competition.id);
                }}
              >
                {competition.status === 'active' ? 'Edit Active Competition' : 'Edit Draft'}
              </button>
            )}

            {competition.status === 'active' && typeof onParticipate === 'function' && (
              <button
                type="button"
                className="action-button primary-action"
                style={{ marginTop: 10 }}
                onClick={(event) => {
                  event.stopPropagation();
                  onParticipate(competition.id);
                }}
              >
                🚀 Participate Now
              </button>
            )}

            {typeof onViewMediaSources === 'function' && (
              <button
                type="button"
                className="action-button"
                style={{ marginTop: 8 }}
                onClick={(event) => {
                  event.stopPropagation();
                  onViewMediaSources(competition.id);
                }}
              >
                View Media Sources
              </button>
            )}

            {typeof onViewSubmissions === 'function' && (
              <button
                type="button"
                className="action-button"
                style={{ marginTop: 8 }}
                onClick={(event) => {
                  event.stopPropagation();
                  onViewSubmissions(competition.id);
                }}
              >
                View All Submissions
              </button>
            )}
          </>
        ))}
      </div>
    </div>
  );
};

function getStatusBadgeClass(status) {
  const statusMap = {
    draft: 'status-draft',
    active: 'status-active',
    judging: 'status-judging',
    completed: 'status-completed',
    cancelled: 'status-cancelled'
  };
  return statusMap[status] || 'status-draft';
}

function formatDate(timestamp) {
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function getCardTheme(competition) {
  if (!competition || !competition.category) {
    return 'video';
  }

  if (competition.category === 'engagement') {
    return 'engagement';
  }

  if (competition.category === 'channel') {
    return 'channel';
  }

  if (competition.category === 'custom') {
    return 'custom';
  }

  return 'video';
}

function getCategoryIcon(category) {
  if (category === 'engagement') {
    return '🔥';
  }

  if (category === 'channel') {
    return '📺';
  }

  if (category === 'custom') {
    return '✨';
  }

  return '🎬';
}

function formatCategoryLabel(category) {
  if (!category) {
    return 'Video Challenge';
  }

  return category.charAt(0).toUpperCase() + category.slice(1) + ' Challenge';
}

function getParticipationUrgency(endDate) {
  const end = Number(endDate || 0);
  if (!end) {
    return 'Dates to be announced';
  }

  const now = Date.now();
  const diff = end - now;
  if (diff <= 0) {
    return 'Competition closed';
  }

  const days = Math.ceil(diff / (24 * 60 * 60 * 1000));
  if (days <= 1) {
    return 'Ends within 24 hours';
  }

  return `${days} days left to submit`;
}

function formatHashtag(hashtag) {
  if (!hashtag) {
    return '#challenge';
  }

  return hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
}

export default CompetitionCard;